import React from "react";
import {
    Form,
    Menu,
    MenuItem,
    Popover,
    Text,
    VisuallyHidden,
} from "react-aria-components";
import { Autocomplete } from "react-aria-components/Autocomplete";
import { msgid, ngettext, t } from "ttag";

import { Context } from "@reactivated";

import { Clickable } from "../Clickables";
import { Highlighted, SearchField, findMatchingThing } from "../Search";
import { useAlgoliaSearch } from "../Search/useAlgoliaSearch";
import { Separator } from "../Separator";
import { Svg } from "../Svg";

import styles from "./SearchModal.module.scss";

export const SearchModal = (props: React.ComponentProps<typeof Popover>) => {
    const context = React.useContext(Context);
    const {
        query,
        setQuery,
        items,
        totalResults,
        substituteQuery,
        activeQuery,
        hasSearched,
    } = useAlgoliaSearch({ algoliaConfig: context.algolia });
    const formRef = React.useRef<HTMLFormElement>(null);

    return (
        <Popover
            {...props}
            className={styles.modal}
            offset={0}
            crossOffset={-24}
            containerPadding={0}
            shouldFlip={false}
            // shouldUpdatePosition={false}
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            <Form
                ref={formRef}
                onSubmit={(e) => {
                    const fd = new FormData(e.currentTarget);
                    e.preventDefault();
                    if (!context.search_results_url) return;
                    const searchUrl = new URL(
                        context.search_results_url,
                        window.location.href,
                    );
                    const q = fd.get("search");
                    if (typeof q === "string") {
                        searchUrl.searchParams.set("q", q);
                    }
                    window.location.href = searchUrl.toString();
                }}
            >
                <Autocomplete
                    inputValue={query}
                    onInputChange={setQuery}
                    disableAutoFocusFirst
                >
                    <div className={styles.input}>
                        <SearchField autoFocus>
                            <Clickable slot={null} type="submit">
                                <VisuallyHidden>{t`Search`}</VisuallyHidden>
                                <Svg aria-hidden name="arrow" />
                            </Clickable>
                        </SearchField>
                        {hasSearched && (
                            <Text slot="description" className={styles.counter}>
                                {substituteQuery &&
                                    t`No results for ${query}.  `}
                                {ngettext(
                                    msgid`Showing ${totalResults} result for`,
                                    `Showing ${totalResults} results for`,
                                    totalResults,
                                )}
                                &nbsp;
                                <q>{activeQuery}</q>
                            </Text>
                        )}
                    </div>
                    <Menu
                        className={styles.results}
                        renderEmptyState={() =>
                            hasSearched ? <p>{t`No results.`}</p> : null
                        }
                    >
                        {items.length ? <Separator /> : null}
                        {items.map((item) => (
                            <>
                                <MenuItem
                                    key={item.objectID}
                                    textValue={
                                        findMatchingThing(item) ?? item.title
                                    }
                                    id={item.objectID}
                                    className={styles.item}
                                    href={item.url}
                                >
                                    <Text slot="label" className={styles.title}>
                                        <Highlighted
                                            highlight={
                                                item._highlightResult?.title
                                            }
                                        >
                                            {item.title}
                                        </Highlighted>
                                    </Text>

                                    {item.subtitle && (
                                        <Text
                                            slot="description"
                                            className={styles.subtitle}
                                        >
                                            <Highlighted
                                                highlight={
                                                    item._highlightResult
                                                        ?.subtitle
                                                }
                                            >
                                                {item.subtitle}
                                            </Highlighted>
                                        </Text>
                                    )}
                                </MenuItem>
                                <Separator />
                            </>
                        ))}
                        {items.length > 0 && (
                            <MenuItem
                                onAction={() =>
                                    formRef.current?.requestSubmit()
                                }
                                className={styles.showAll}
                            >
                                {t`Show all results for '${query}'`}
                            </MenuItem>
                        )}
                    </Menu>
                </Autocomplete>
            </Form>
        </Popover>
    );
};
